<template>
  <!-- 一覧の列見出しに置く、Excelのオートフィルターのようなメニュー（並べ替え・絞り込み） -->
  <v-menu v-model="open" offset-y :close-on-content-click="false" max-width="300">
    <template v-slot:activator="{ on, attrs }">
      <span class="column-filter__activator" v-bind="attrs" v-on="on">
        {{ column.text }}
        <v-icon v-if="sortDirection" x-small>{{ sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
        <v-icon x-small :color="active ? 'secondary' : 'grey'">{{ active ? 'mdi-filter' : 'mdi-filter-outline' }}</v-icon>
      </span>
    </template>

    <v-card class="column-filter__card">
      <div class="px-3 pt-3">
        <v-btn text small block class="justify-start" :color="sortDirection === 'asc' ? 'secondary' : ''" @click="sort('asc')">
          <v-icon left small>mdi-sort-ascending</v-icon>昇順で並べ替え
        </v-btn>
        <v-btn text small block class="justify-start" :color="sortDirection === 'desc' ? 'secondary' : ''" @click="sort('desc')">
          <v-icon left small>mdi-sort-descending</v-icon>降順で並べ替え
        </v-btn>
      </div>
      <v-divider class="my-2" />

      <div class="px-3">
        <!-- 値のチェックリスト（チェックを外した値を非表示にする） -->
        <template v-if="column.type === 'select'">
          <v-text-field v-model="search" dense hide-details clearable placeholder="値を検索" prepend-inner-icon="mdi-magnify" class="mb-1" />
          <v-checkbox
            :input-value="allChecked"
            :indeterminate="!allChecked && checkedCount > 0"
            label="（すべて選択）"
            dense
            hide-details
            class="mt-1"
            @change="toggleAll"
          />
          <div class="column-filter__values">
            <v-checkbox
              v-for="option in visibleOptions"
              :key="option"
              :input-value="!excluded.includes(option)"
              :label="option"
              dense
              hide-details
              class="mt-0"
              @change="toggleOption(option, $event)"
            />
          </div>
        </template>

        <!-- 文字を含む -->
        <v-text-field
          v-else-if="column.type === 'text'"
          :value="filter || ''"
          dense
          clearable
          autofocus
          label="この文字を含む"
          @input="$emit('filter', $event || '')"
        />

        <!-- 期間 -->
        <template v-else-if="column.type === 'date'">
          <v-text-field :value="(filter && filter.from) || ''" type="date" dense label="この日から" @input="updateDate('from', $event)" />
          <v-text-field :value="(filter && filter.to) || ''" type="date" dense label="この日まで" @input="updateDate('to', $event)" />
        </template>
      </div>

      <v-card-actions>
        <v-btn text small :disabled="!active && !sortDirection" @click="clear">クリア</v-btn>
        <v-spacer />
        <v-btn text small color="secondary" @click="open = false">閉じる</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import { BLANK_LABEL, isFilterActive } from '@/mixins/columnFilterList'

export default {
  props: {
    // { text: 見出し, value: 列名, type: 'select' | 'text' | 'date', label: item => 文字列 }
    column: { type: Object, required: true },
    // 一覧の全件（チェックリストの選択肢を作るのに使う）
    items: { type: Array, default: () => [] },
    filter: { type: [Array, String, Object], default: null },
    sortDirection: { type: String, default: null },
  },

  data() {
    return {
      open: false,
      search: '',
    }
  },

  computed: {
    active() {
      return isFilterActive(this.column.type, this.filter)
    },
    excluded() {
      return Array.isArray(this.filter) ? this.filter : []
    },
    options() {
      const values = new Set(this.items.map(item => {
        const label = this.column.label(item)
        return label == null || label === '' ? BLANK_LABEL : String(label)
      }))
      return [...values].sort((a, b) => a.localeCompare(b, 'ja', { numeric: true }))
    },
    visibleOptions() {
      if (!this.search) return this.options
      return this.options.filter(option => option.toLowerCase().includes(this.search.toLowerCase()))
    },
    checkedCount() {
      return this.options.filter(option => !this.excluded.includes(option)).length
    },
    allChecked() {
      return this.excluded.length === 0
    },
  },

  methods: {
    sort(direction) {
      this.$emit('sort', this.sortDirection === direction ? null : direction)
    },
    toggleOption(option, checked) {
      const excluded = checked ? this.excluded.filter(v => v !== option) : [...this.excluded, option]
      this.$emit('filter', excluded)
    },
    // 検索で絞り込み中は、表示されている値だけをまとめて選択・解除する
    toggleAll(checked) {
      const targets = this.visibleOptions
      const excluded = checked
        ? this.excluded.filter(v => !targets.includes(v))
        : [...new Set([...this.excluded, ...targets])]
      this.$emit('filter', excluded)
    },
    updateDate(key, value) {
      this.$emit('filter', { ...(this.filter || {}), [key]: value || '' })
    },
    clear() {
      this.search = ''
      this.$emit('filter', this.column.type === 'select' ? [] : this.column.type === 'date' ? {} : '')
      if (this.sortDirection) this.$emit('sort', null)
    },
  },
}
</script>

<style lang="stylus" scoped>
.column-filter__activator
  cursor pointer
  user-select none
  white-space nowrap

.column-filter__card
  min-width 240px

.column-filter__values
  max-height 260px
  overflow-y auto
</style>
